import { memo, useCallback, useEffect } from 'react';
import { Field, FieldMetaProps, FieldProps, FormikContextType, useField, useFormikContext } from 'formik';
import { Input, MLPVersion, useTranslation } from '@nextgen-web-framework/all';
import { DynamicObjectInterface, UsageStatus } from '@commerce-profile-webcomponents/sdk';
import { useSelector } from 'react-redux';
import { type AppState } from '@nextgen-web-framework/core';
import PhoneNumberInput from '../Common/PhoneNumberInput/PhoneNumberInput';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import { inputErrorMessage, inputErrorStatus } from '../Common/InputErrorComponent/utils';
import { COAPP_REGISTRATION_STEP_NAME, VALIDATION_VALID_STRING } from '../../utils/Constants';
import { updateCoApplicantPayload } from '../../utils/registrationUtils';
import { APICallType } from '../Registration/types/propTypes';
import { SecondaryContactFieldProps, UsernameFormikContextValues } from './types/propTypes';

const SecondaryContactField = ({
  isPhonePrimaryContact,
  defaultCountryDialCode,
  nextHandler,
}: SecondaryContactFieldProps) => {
  const { status, values, handleBlur, setFieldValue, validateField }: FormikContextType<UsernameFormikContextValues> =
    useFormikContext();
  const t = useTranslation();
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const currentStepName = registrationFlowData?.currentStepName;
  const patchVersion = registrationFlowData?.userData?.patchVersion;

  const globalErrorHandler = useGlobalErrorHandler();
  const [, , phoneHandler] = useField('phone');
  const [, , emailHander] = useField('email');
  const [, secondaryContactValidatorMeta] = useField('secondaryContactValidator');

  useEffect(() => {
    // condition for making phone optional if email is primary
    !isPhonePrimaryContact && !values?.phone && setFieldValue('secondaryContactValidator', VALIDATION_VALID_STRING);
  }, [isPhonePrimaryContact, values?.phone]);

  const getTrimNumber = useCallback(
    (phoneNumber?: string) =>
      phoneNumber?.startsWith(defaultCountryDialCode) ? phoneNumber.slice(defaultCountryDialCode.length) : phoneNumber,
    [defaultCountryDialCode],
  );

  const onPhoneNumberChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const phoneValue = e.target.value;
      phoneHandler.setValue(phoneValue ? defaultCountryDialCode + phoneValue : phoneValue);
      await validateField('phone');
      setFieldValue('secondaryContactValidator', VALIDATION_VALID_STRING);
    },
    [phoneHandler, defaultCountryDialCode],
  );

  const onEmailChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      emailHander.setValue(e.target.value);
      await validateField('email');
      setFieldValue('secondaryContactValidator', VALIDATION_VALID_STRING);
    },
    [emailHander],
  );

  const getSecondaryField = (name: string, value: string) =>
    name === 'email' ? value : defaultCountryDialCode + value;

  const handleUserExistenceError = (usageStatus?: UsageStatus) => {
    setFieldValue(
      'secondaryContactValidator',
      usageStatus
        ? `profile.RegistrationPersonalDetailsStep.checkUserMessage.${usageStatus}`
        : 'profile.RegistrationPersonalDetailsStep.duplicateUser',
    );
  };

  const onBlurSecondaryContact = useCallback(
    async (event: React.FocusEvent<HTMLInputElement>, meta: FieldMetaProps<DynamicObjectInterface>) => {
      const { name, value: currentValue } = event.target;
      const fieldValue = getSecondaryField(name, currentValue);
      handleBlur(event);
      setFieldValue('secondaryContactValidator', VALIDATION_VALID_STRING);
      if (meta.error || !currentValue) {
        return;
      }
      try {
        let regPayload = {
          version: patchVersion,
          [isPhonePrimaryContact ? 'email' : 'phone']: fieldValue,
        };
        if (currentStepName === COAPP_REGISTRATION_STEP_NAME) {
          regPayload = updateCoApplicantPayload(regPayload);
        }
        const res = await nextHandler?.(regPayload, undefined, true, false, APICallType.PATCH);
        if (res?.responseBody?.code) {
          handleUserExistenceError(res?.responseBody?.code);
        }
      } catch {
        setFieldValue('secondaryContactValidator', 'profile.common.errorHeading');
      }
    },
    [globalErrorHandler, handleBlur, defaultCountryDialCode],
  );

  const renderEmailField = () => (
    <Field type="email" name="email">
      {({ meta }: FieldProps) => (
        <Input
          label={t('profile.registration.username.emailText')}
          type="email"
          name="email"
          placeholder={t('profile.registration.username.emailText')}
          value={values?.email}
          onBlur={(e) => onBlurSecondaryContact(e, meta)}
          onChange={onEmailChange}
          autoComplete="off"
          status={inputErrorStatus(meta.touched, meta.error || secondaryContactValidatorMeta.error)}
          message={inputErrorMessage(t, meta.touched, meta.error || secondaryContactValidatorMeta.error)}
          disabled={!status?.enableField}
        />
      )}
    </Field>
  );

  const renderPhoneField = () => (
    <Field type="text" name="phone">
      {({ meta }: FieldProps) => (
        <PhoneNumberInput
          name="phone"
          data-testid="phone-testid"
          dropdownValue={`(${defaultCountryDialCode})`}
          dropdownDisabled={true}
          hideDropdown={false}
          mlpVersion={MLPVersion.TWO}
          inputLabel={t('profile.registration.username.mobileText')}
          value={getTrimNumber(values?.phone)}
          onChange={onPhoneNumberChange}
          onBlur={(e) => onBlurSecondaryContact(e, meta)}
          validationStatus={inputErrorStatus(meta.touched, meta.error || secondaryContactValidatorMeta.error)}
          validationMessage={inputErrorMessage(t, meta.touched, meta.error || secondaryContactValidatorMeta.error)}
          disabled={!status?.enableField}
          height={'40px'}
        />
      )}
    </Field>
  );

  return (
    <>
      <Field type="hidden" name="secondaryContactValidator" />
      {isPhonePrimaryContact ? renderEmailField() : renderPhoneField()}
    </>
  );
};

export default memo(SecondaryContactField);
